import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { CodeFolderIcon, MailAtSign02Icon, Male02Icon } from '@hugeicons/core-free-icons';
import { usePortfolio } from '../../../features/portfolio/presentation/context/PortfolioContext';

const navMeta: Record<string, { icon: any; color1: string; color2: string }> = {
  '/about': {
    icon: Male02Icon,
    color1: '#a955ff',
    color2: '#ea51ff',
  },
  '/projects': {
    icon: CodeFolderIcon,
    color1: '#56CCF2',
    color2: '#2F80ED',
  },
  '/contact': {
    icon: MailAtSign02Icon,
    color1: '#FF9966',
    color2: '#FF5E62',
  },
};

export function Header() {
  const { data } = usePortfolio();
  const { copy, navigation } = data.siteContent;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const reduceMotion = useReducedMotion();

  const contentVariants: Variants | undefined = reduceMotion
    ? undefined
    : {
        hidden: {},
        visible: {
          transition: {
            delayChildren: 0.28,
            staggerChildren: 0.045,
          },
        },
      };

  const itemVariants: Variants | undefined = reduceMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 8, scale: 0.88, filter: 'blur(8px)' },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          transition: {
            type: 'spring',
            stiffness: 520,
            damping: 34,
            mass: 0.72,
          },
        },
      };

  useEffect(() => {
    let frame = 0;

    const desktopIsland = window.matchMedia('(min-width: 821px)');

    /** Expands only the desktop island after scrolling; mobile keeps one stable compact width. */
    const updateIsland = () => {
      setIsScrolled(desktopIsland.matches && window.scrollY > 64);
      frame = 0;
    };

    const handleScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(updateIsland);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    desktopIsland.addEventListener('change', updateIsland);
    updateIsland();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      desktopIsland.removeEventListener('change', updateIsland);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <a className="skip-link" href="#main-content">
        {copy.skipToContent}
      </a>
      <motion.header
        className={`site-header${isScrolled ? ' is-expanded' : ''}${menuOpen ? ' has-open-menu' : ''}`}
        initial={false}
      >
        <motion.nav
          className="nav-pill"
          aria-label="Primary navigation"
          initial={
            reduceMotion
              ? false
              : { scaleX: 0.03, scaleY: 0.34, y: -24, filter: 'blur(10px)' }
          }
          animate={
            reduceMotion
              ? {}
              : {
                  scaleX: 1,
                  scaleY: 1,
                  y: 0,
                  filter: 'blur(0px)',
                }
          }
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  type: 'spring',
                  stiffness: 520,
                  damping: 31,
                  mass: 0.78,
                }
          }
          layout
        >
          <motion.div
            className="nav-pill-content"
            variants={contentVariants}
            initial={reduceMotion ? false : 'hidden'}
            animate={reduceMotion ? undefined : 'visible'}
          >
            <motion.div className="nav-content-item nav-brand-item" variants={itemVariants}>
              <NavLink className="brand" to="/" aria-label={`${data.profile.brandName} home`}>
                <span className="brand-mark" aria-hidden="true">
                  {data.profile.logoUrl ? <img src={data.profile.logoUrl} alt="" /> : 'LB'}
                </span>
                <span>{data.profile.brandName}</span>
              </NavLink>
            </motion.div>

            <div className="desktop-navigation">
              <ul className="nav-expand-list">
                {navigation.map((item) => {
                  const meta = navMeta[item.href] || {
                    icon: Male02Icon,
                    color1: '#a955ff',
                    color2: '#ea51ff',
                  };

                  return (
                    <motion.li className="nav-expand-item nav-content-item" key={item.href} variants={itemVariants}>
                      <NavLink
                        className={({ isActive }) => `nav-expand-link${isActive ? ' is-active' : ''}`}
                        to={item.href}
                        style={
                          {
                            '--i': meta.color1,
                            '--j': meta.color2,
                          } as React.CSSProperties
                        }
                      >
                        <span className="nav-icon">
                          <HugeiconsIcon icon={meta.icon} size={18} />
                        </span>
                        <span className="nav-title">{item.label}</span>
                      </NavLink>
                    </motion.li>
                  );
                })}
              </ul>
            </div>

            <div className="nav-actions">
              <motion.div className="nav-content-item desktop-only" variants={itemVariants}>
                <NavLink className="hire-button" to="/contact">
                  {copy.hireMe}
                </NavLink>
              </motion.div>
              <motion.div className="nav-content-item mobile-only" variants={itemVariants}>
                <button
                  className="menu-button"
                  type="button"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-navigation"
                  aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  {menuOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.nav>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              id="mobile-navigation"
              className="mobile-navigation mobile-only"
              initial={{ y: -16, scaleX: 0, scaleY: 0, borderRadius: 999, filter: 'blur(12px)' }}
              animate={{
                y: [-16, 6, -2, 0],
                scaleX: [0, 0.82, 1.035, 0.985, 1],
                scaleY: [0, 0.28, 1.08, 0.96, 1],
                borderRadius: [999, 999, 34, 26, 28],
                filter: ['blur(12px)', 'blur(7px)', 'blur(2px)', 'blur(0px)', 'blur(0px)'],
              }}
              exit={{ y: -10, scaleX: 0, scaleY: 0, borderRadius: 999, filter: 'blur(10px)' }}
              transition={{
                duration: 0.56,
                ease: [0.22, 1, 0.36, 1] as const,
                times: [0, 0.24, 0.52, 0.78, 1],
              }}
            >
              {navigation.map((item) => (
                <NavLink
                  key={item.href}
                  className={({ isActive }) => `mobile-nav-link${isActive ? ' is-active' : ''}`}
                  to={item.href}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
              <NavLink className="hire-button" to="/contact" onClick={() => setMenuOpen(false)}>
                {copy.hireMe}
              </NavLink>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
}
